import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakerHighIcon } from '@phosphor-icons/react/SpeakerHigh';
import { StopIcon } from '@phosphor-icons/react/Stop';

/** A Ukrainian voice of the system, if there is one (macOS and iOS have «Lesya»). */
function useUkrainianVoice() {
  const [voice, setVoice] = useState<SpeechSynthesisVoice>();
  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const pick = () =>
      setVoice(
        window.speechSynthesis.getVoices().find((v) => v.lang.toLowerCase().startsWith('uk')),
      );
    pick();
    window.speechSynthesis.addEventListener('voiceschanged', pick);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', pick);
  }, []);
  return voice;
}

/**
 * «Для всіх дітей»: reads a task aloud with the system's Ukrainian voice — for those who see badly,
 * read slowly or just listen better. Hidden when the device has no Ukrainian voice.
 */
export function SpeakButton({ text, className = '' }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const voice = useUkrainianVoice();
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  if (!voice) return null;
  const toggle = () => {
    window.speechSynthesis.cancel();
    if (speaking) {
      setSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 0.95;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={speaking}
      className={`inline-flex h-9 items-center gap-2 rounded-full pr-4 pl-1 text-[13.5px] font-medium transition-colors ${speaking ? 'bg-primary text-white' : 'bg-white text-ink ring-1 ring-[#E4E0D7] ring-inset hover:bg-[#FAF9F6]'} ${className}`}
    >
      {/* «Форми 2»: a pill with its icon in a ring */}
      <span className="flex size-7 items-center justify-center rounded-full ring-[1.5px] ring-current ring-inset">
        {speaking ? (
          <StopIcon size={12} weight="fill" aria-hidden />
        ) : (
          <SpeakerHighIcon size={13} weight="fill" aria-hidden />
        )}
      </span>
      {speaking ? t('common.stopReading') : t('common.readAloud')}
    </button>
  );
}
